import React, { useRef, useState } from "react";
import EditableSelect from "../lockbox-inventory/editable-select";
import { Calendar, Mail, Plus, X } from "lucide-react";
import ClientSuggesstionPopup from "../ClientSuggesstionPopup";
import { formatDates } from "../../utils/helper";

// Format a raw numeric string/number as "$1,234,567" for display in
// the currency Field. Empty input renders empty (no zero placeholder).
const formatCurrencyForInput = (val) => {
  if (val === "" || val == null) return "";
  const n = Number(String(val).replace(/[^\d.]/g, ""));
  if (Number.isNaN(n)) return "";
  return "$" + n.toLocaleString("en-US");
};

// Strip everything except digits from the typed string so the stored
// value is always parseable as a number by downstream consumers
// (Commission Breakdown, formatCurrency, etc.).
const parseCurrencyFromInput = (str) => {
  return String(str).replace(/[^\d]/g, "");
};

const Field = React.memo(
  ({ label, field, type = "text", isBtn = false, value, onChange, isEdit }) => {
    const hiddenDateRef = useRef(null);

    const openDatePicker = () => {
      if (hiddenDateRef.current) {
        hiddenDateRef.current.showPicker
          ? hiddenDateRef.current.showPicker()
          : hiddenDateRef.current.click();
      }
    };
    return (
      <div className="rounded-lg w-full sm:w-[48%]">
        <p className="text-sm text-gray-500">{label}</p>

        {isEdit ? (
          <div className="flex items-center gap-x-2">
            {type === "currency" ? (
              <input
                type="text"
                inputMode="numeric"
                value={formatCurrencyForInput(value)}
                onChange={(e) =>
                  onChange(field, parseCurrencyFromInput(e.target.value))
                }
                className="rounded-lg px-2 py-1.5 w-full text-sm mt-1 border-2 border-gray-300 focus:border-[#1E293B] outline-none"
              />
            ) : type === "date" ? (
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={formatDates(value)}
                  readOnly
                  onClick={openDatePicker}
                  className="rounded-lg px-2 py-1.5 w-full text-sm mt-1 border-2 border-gray-300 focus:border-[#1E293B] outline-none cursor-pointer"
                />

                <div
                  className="absolute top-3 right-3 cursor-pointer"
                  onClick={openDatePicker}
                >
                  <Calendar size={18} />
                </div>

                <input
                  type="date"
                  ref={hiddenDateRef}
                  value={value || ""}
                  onChange={(e) => onChange(field, e.target.value)}
                  className="absolute left-0 top-0 w-0 h-0 opacity-0 pointer-events-none"
                />
              </div>
            ) : (
              <input
                value={value}
                onChange={(e) => onChange(field, e.target.value)}
                type={type}
                className="rounded-lg px-2 py-1.5 w-full text-sm mt-1 border-2 focus:border-[#1E293B] outline-none"
              />
            )}

            {isBtn && (
              <button className="text-[#475569] w-fit h-full px-2 py-2 hover:border-indigo-600 hover:text-indigo-600 hover:bg-gray-100 rounded-lg  flex border border-gray-300 items-center gap-x-1.5">
                <Mail className="size-[14px]" />
                <span className="text-xs font-medium whitespace-nowrap">
                  Notify Vendor
                </span>
              </button>
            )}
          </div>
        ) : (
          <p className="font-semibold">{value || "-"}</p>
        )}
      </div>
    );
  },
);

function BuyerForm({
  isEdit,
  formData,
  onFormChange,
  contactType,
  contactSync,
  onContactSelection,
  selectedContacts,
  participants,
  setParticipants,
  deal,
}) {
  const recission = ["Yes", "No"];
  const [openRecission, setOpenRecission] = useState(false);
  const appraisal = ["Yes", "No"];
  const [openAppraisal, setOpenAppraisal] = useState(false);
  const yesNo = ["Yes", "No"];
  const [openInspection, setOpenInspection] = useState(false);
  const [openTypeDropdown, setOpenTypeDropdown] = useState(null);

  const handleAddParticipant = () => {
    setParticipants((prev) => [
      ...prev,
      {
        id: Date.now(),
        typeId: "",
        type: "",
        isCustom: true,
        fullName: "",
        phone: "",
        email: "",
        search: {
          fullName: "",
          phone: "",
          email: "",
        },
        dropdown: {
          field: null,
          index: null,
        },
        filtered: [],
      },
    ]);
  };

  const handleRemoveParticipant = (index) => {
    setParticipants((prev) => {
      const removedItem = prev[index];

      const updated = prev.filter((_, idx) => idx !== index);

      if (onContactSelection && removedItem) {
        onContactSelection({
          typeId: removedItem.id,
          remove: true,
        });
      }

      return updated;
    });
  };

  const handleParticipantChange = (index, field, value) => {
    setParticipants((prev) => {
      const updated = prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      );

      const selected = updated[index];

      if (field === "type" && selected && onContactSelection) {
        onContactSelection({
          typeId: selected.id,
          type: value,
          contactId: selected.contactId || "",
        });
      }

      return updated;
    });
  };

  const handleParticipantSearch = (index, field, value) => {
    setParticipants((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;

        const val = value.toLowerCase();
        const list = contactSync || [];

        const filtered = list.filter((c) => {
          const name = (c.name || "").toLowerCase();
          const phone = (c.phone || "").toLowerCase();
          const email = (c.email || "").toLowerCase();

          if (field === "fullName") return name.includes(val);
          if (field === "phone") return phone.includes(val);
          if (field === "email") return email.includes(val);
        });

        return {
          ...item,
          search: {
            ...item.search,
            [field]: value,
          },
          filtered,
          dropdown: {
            field,
            index: item.dropdown?.index ?? null,
          },
        };
      }),
    );
  };

  const handleSelectContact = (index, contact) => {
    setParticipants((prev) => {
      const updated = prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              contactId: contact.id,
              fullName: contact.name || "",
              phone: contact.phone || "",
              email: contact.email || "",
              search: {
                fullName: contact.name || "",
                phone: contact.phone || "",
                email: contact.email || "",
              },
              dropdown: {
                field: null,
                index: null,
              },
              filtered: [],
            }
          : item,
      );

      const selected = updated[index];

      if (selected && onContactSelection) {
        onContactSelection({
          typeId: selected.id,
          type: selected.type || "",
          contactId: contact.id,
        });
      }

      return updated;
    });
  };

  return (
    <div className=" px-1 py-3">
      <h4 className="text-base font-semibold mt-5">
        Under Contract Milestones
      </h4>

      {/* Financials at the top of the milestones list. Sales Price +
          Earnest Money Amount come from turn-in but stay editable here
          for post-acceptance adjustments. EM Received Date is new —
          tracked separately from the agreed amount. */}
      <div className="flex justify-between mt-3">
        <Field
          label="Sales Price"
          field="offerPrice"
          value={formData.offerPrice}
          type="currency"
          onChange={onFormChange}
          isEdit={isEdit}
        />
        <Field
          label="Earnest Money Amount"
          field="earnestMoney"
          value={formData.earnestMoney}
          type="currency"
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      <div className="flex justify-between mt-3">
        <Field
          label="EM Received Date"
          field="earnestMoneyReceivedDate"
          value={formData.earnestMoneyReceivedDate}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
        <Field
          label="Final Acceptance Date"
          field="finalAcceptanceDate"
          value={formData.finalAcceptanceDate}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      <div className="flex justify-between mt-3">
        <Field
          label="Final Walkthrough"
          field="finalWalkthrough"
          value={formData.finalWalkthrough}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
        <Field
          label="Closing Date"
          field="closingdate"
          value={formData.closingdate}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      {/* Row 2: Buyer Inspection Yes/No + the inspection date next to it.
          Sits above Rescission per the user's layout request. */}
      <div className="flex justify-between mt-3">
        <div className="relative w-[48%]">
          <p className="text-sm text-gray-500 pb-1">Buyer Inspection</p>
          {isEdit ? (
            <>
              <div
                onClick={() => setOpenInspection(!openInspection)}
                className={`w-full border-2 border-gray-300 rounded-lg py-1 px-2 cursor-pointer ${formData.inspection === "" ? "text-gray-400" : ""}`}
              >
                {formData.inspection || "Select"}
              </div>

              <EditableSelect
                style={{ top: "75px", left: "0px" }}
                visible={openInspection}
                activateoutsidecontainerclick={() =>
                  setOpenInspection(!openInspection)
                }
                status={yesNo}
                onClickcorrect={(value) => {
                  onFormChange("inspection", value);
                  setOpenInspection(false);
                }}
              />
            </>
          ) : (
            <p className="font-semibold">{formData.inspection || "-"}</p>
          )}
        </div>
        <Field
          label="Buyer Inspection Date"
          field="buyerInspection"
          value={formData.buyerInspection}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      {/* Inspection Deadline is the contingency end date — drives the
          "Inspection Deadline" countdown in Important Dates and the
          inspectionContingencyEnd placeholder used in acceptance
          emails. Sits between the Buyer Inspection row and the
          Rescission row so the two inspection-related dates are
          adjacent. */}
      <div className="flex justify-between mt-3">
        <Field
          label="Inspection Deadline"
          field="inspectionDeadline"
          value={formData.inspectionDeadline}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      {/* Row 3: Rescission Period + deadline. */}
      <div className="flex justify-between mt-3">
        <div className="relative w-[48%]">
          <p className="text-sm text-gray-500 pb-1">Rescission Period</p>
          {isEdit ? (
            <>
              <div
                onClick={() => setOpenRecission(!openRecission)}
                className={`w-full border-2 border-gray-300 rounded-lg py-1 px-2 cursor-pointer ${formData.recissionPeriod === "" ? "text-gray-400" : ""}`}
              >
                {formData.recissionPeriod || "Select"}
              </div>

              <EditableSelect
                style={{ top: "75px", left: "0px" }}
                visible={openRecission}
                activateoutsidecontainerclick={() =>
                  setOpenRecission(!openRecission)
                }
                status={recission}
                onClickcorrect={(value) => {
                  onFormChange("recissionPeriod", value);
                  setOpenRecission(false);
                }}
              />
            </>
          ) : (
            <p className="font-semibold">{formData.recissionPeriod || "-"}</p>
          )}
        </div>
        <Field
          label="Rescission Deadline"
          field="rescissionDeadline"
          value={formData.rescissionDeadline}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      {/* Row 4: Financing Type + Financing Deadline. */}
      <div className="flex justify-between mt-3">
        <Field
          label="Financing Type"
          field="financingType"
          value={formData.financingType}
          type="text"
          onChange={onFormChange}
          isEdit={isEdit}
        />
        <Field
          label="Financing Deadline"
          field="financingDeadline"
          value={formData.financingDeadline}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      {/* Row 5: Appraisal date + Appraisal Complete? selector. */}
      <div className="flex justify-between mt-3">
        <Field
          label="Appraisal"
          field="appraisal"
          value={formData.appraisal}
          type="date"
          onChange={onFormChange}
          isEdit={isEdit}
        />
        <div className="relative w-[48%]">
          <p className="text-sm text-gray-500">Appraisal Complete?</p>
          {isEdit ? (
            <>
              <div
                onClick={() => setOpenAppraisal(!openAppraisal)}
                className={`w-full border-2 border-gray-300 rounded-lg py-1 px-2 cursor-pointer ${formData.appraisalComplete === "" ? "text-gray-400" : ""}`}
              >
                {formData.appraisalComplete || "Select"}
              </div>

              <EditableSelect
                style={{ top: "75px", left: "0px" }}
                visible={openAppraisal}
                activateoutsidecontainerclick={() =>
                  setOpenAppraisal(!openAppraisal)
                }
                status={appraisal}
                onClickcorrect={(value) => {
                  onFormChange("appraisalComplete", value);
                  setOpenAppraisal(false);
                }}
              />
            </>
          ) : (
            <p className="font-semibold">{formData.appraisalComplete || "-"}</p>
          )}
        </div>
      </div>

      <h4 className="text-base font-semibold mt-5">Contacts</h4>

      <div className="flex flex-wrap gap-y-3 justify-between mt-3">
        {participants.map((item, index) => (
          <div
            key={item.id}
            className="w-full sm:w-[49%] border border-gray-300 rounded-xl p-3"
          >
            <div className="flex items-center justify-between relative">
              <div className="flex items-center gap-x-3 w-[85%]">
                <div className="bg-gray-200 rounded-full text-gray-500 font-medium size-9 flex items-center justify-center text-sm uppercase">
                  <p>
                    {isEdit
                      ? item.type
                        ? item.type
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .slice(0, 2)
                        : "NA"
                      : item.fullName
                        ? item.fullName
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .slice(0, 2)
                        : "NA"}
                  </p>
                </div>

                <div className="flex-1">
                  {isEdit ? (
                    <div className="relative w-full">
                      <div
                        onClick={() =>
                          setOpenTypeDropdown(
                            openTypeDropdown === index ? null : index,
                          )
                        }
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm w-full cursor-pointer bg-white"
                      >
                        {item.type || "Select Type"}
                      </div>

                      <EditableSelect
                        style={{ top: "30px", left: "0px" }}
                        visible={openTypeDropdown === index}
                        activateoutsidecontainerclick={() =>
                          setOpenTypeDropdown(null)
                        }
                        status={contactType
                          .filter((ct) => {
                            const clientType = (
                              ct.clientType || ""
                            ).toLowerCase();
                            return (
                              clientType === "buyer" || clientType === "both"
                            );
                          })
                          .map((ct) => ct.type)}
                        onClickcorrect={(value) => {
                          const selectedType = contactType.find(
                            (ct) => ct.type === value,
                          );

                          handleParticipantChange(index, "type", value);
                          handleParticipantChange(
                            index,
                            "typeId",
                            selectedType?.id || item.id,
                          );

                          setOpenTypeDropdown(null);
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-400 mt-0.5">
                        {item.type || "Contact"}
                      </p>
                      <p className="text-sm font-medium text-gray-700">
                        {item.fullName || "-"}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {isEdit && (
                <button
                  onClick={() => handleRemoveParticipant(index)}
                  className="text-red-500 bg-gray-100 absolute top-0 right-0 size-5 flex items-center justify-center rounded-full text-lg font-bold"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="mt-3 space-y-3">
              {isEdit && (
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>

                  <div className="relative">
                    <div
                      onClick={() =>
                        setParticipants((prev) =>
                          prev.map((p) =>
                            p.id === item.id
                              ? {
                                  ...p,
                                  dropdown: {
                                    field: "fullName",
                                    index,
                                  },
                                  filtered: contactSync || [],
                                }
                              : p,
                          ),
                        )
                      }
                      className="border p-2 w-full rounded cursor-pointer bg-white text-sm min-h-[38px] flex items-center text-gray-700"
                    >
                      {item.fullName || "Select name"}
                    </div>

                    {item.dropdown?.field === "fullName" &&
                      item.dropdown?.index === index && (
                        <ClientSuggesstionPopup
                          style={{ top: "45px", left: "0px" }}
                          fieldtype="name"
                          data={contactSync || []}
                          visible={true}
                          name={item.search?.fullName}
                          phone={item.search?.phone}
                          email={item.search?.email}
                          activateoutsidecontainerclick={() => {
                            setParticipants((prev) =>
                              prev.map((p) =>
                                p.id === item.id
                                  ? {
                                      ...p,
                                      dropdown: {
                                        field: null,
                                        index: null,
                                      },
                                    }
                                  : p,
                              ),
                            );
                          }}
                          onSearch={(val) =>
                            handleParticipantSearch(index, "fullName", val)
                          }
                          onClickcorrect={(c) => handleSelectContact(index, c)}
                        />
                      )}
                  </div>
                </div>
              )}

              <div className={`${!isEdit ? "flex gap-x-16" : ""}`}>
                <p className="text-sm text-gray-500">Phone</p>

                {isEdit ? (
                  <div className="relative">
                    <div
                      onClick={() =>
                        setParticipants((prev) =>
                          prev.map((p) =>
                            p.id === item.id
                              ? {
                                  ...p,
                                  dropdown: {
                                    field: "phone",
                                    index,
                                  },
                                  filtered: contactSync || [],
                                }
                              : p,
                          ),
                        )
                      }
                      className="border p-2 w-full rounded cursor-pointer bg-white text-sm min-h-[38px] flex items-center text-gray-700"
                    >
                      {item.phone || "Select phone"}
                    </div>

                    {item.dropdown?.field === "phone" &&
                      item.dropdown?.index === index && (
                        <ClientSuggesstionPopup
                          style={{ top: "45px", left: "0px" }}
                          fieldtype="phone"
                          data={contactSync || []}
                          visible={true}
                          phone={item.search?.phone}
                          activateoutsidecontainerclick={() => {
                            setParticipants((prev) =>
                              prev.map((p) =>
                                p.id === item.id
                                  ? {
                                      ...p,
                                      dropdown: {
                                        field: null,
                                        index: null,
                                      },
                                    }
                                  : p,
                              ),
                            );
                          }}
                          onSearch={(val) =>
                            handleParticipantSearch(index, "phone", val)
                          }
                          onClickcorrect={(c) => handleSelectContact(index, c)}
                        />
                      )}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-indigo-500">
                    {item.phone || "-"}
                  </p>
                )}
              </div>

              <div className={`${!isEdit ? "flex gap-x-16" : ""}`}>
                <p className="text-sm text-gray-500">Email</p>

                {isEdit ? (
                  <div className="relative">
                    <div
                      onClick={() =>
                        setParticipants((prev) =>
                          prev.map((p) =>
                            p.id === item.id
                              ? {
                                  ...p,
                                  dropdown: {
                                    field: "email",
                                    index,
                                  },
                                  filtered: contactSync || [],
                                }
                              : p,
                          ),
                        )
                      }
                      className="border p-2 w-full rounded cursor-pointer bg-white text-sm min-h-[38px] flex items-center text-gray-700"
                    >
                      {item.email || "Select email"}
                    </div>

                    {item.dropdown?.field === "email" &&
                      item.dropdown?.index === index && (
                        <ClientSuggesstionPopup
                          style={{ top: "45px", left: "0px" }}
                          fieldtype="email"
                          data={contactSync || []}
                          visible={true}
                          email={item.search?.email}
                          activateoutsidecontainerclick={() => {
                            setParticipants((prev) =>
                              prev.map((p) =>
                                p.id === item.id
                                  ? {
                                      ...p,
                                      dropdown: {
                                        field: null,
                                        index: null,
                                      },
                                    }
                                  : p,
                              ),
                            );
                          }}
                          onSearch={(val) =>
                            handleParticipantSearch(index, "email", val)
                          }
                          onClickcorrect={(c) => handleSelectContact(index, c)}
                        />
                      )}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-indigo-500 break-all">
                    {item.email || "-"}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isEdit && (
        <div>
          <button
            type="button"
            onClick={() => {
              if (!isEdit) return;
              handleAddParticipant();
            }}
            className={`bg-white flex justify-between items-center gap-x-1.5 border border-gray-300 text-gray-500 font-semibold rounded-lg px-3 py-1.5 mt-4 hover:bg-gray-50 ${
              !isEdit ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <Plus size={16} /> Add Participant
          </button>
        </div>
      )}
    </div>
  );
}

export default BuyerForm;
